import { CartService } from '@/data/services/cart.service';
import { I18NService } from '@/data/services/i18n.service';
import { ShopService } from '@/data/services/shop.service';
import { WishlistService } from '@/data/services/wishlist.service';
import { setLocale } from '@/plugins/vee-validate';
import store, { State } from '@/store';
import { YFC } from '@/utils/constants';
import { Mutex } from 'async-mutex';
import { classToPlain, plainToClass } from 'class-transformer';
import { Component, Inject, Vue } from 'vue-property-decorator';
import { AuthService, StorageManager } from 'yellowfin-ui-lib';
import { Customer } from 'yf-commerce-model-ui-lib';

@Component
export default class StateComponent extends Vue {
	@Inject("AuthService") authService!: AuthService;
	@Inject("CartService") cartService!: CartService;
	@Inject("WishlistService") wishlistService!: WishlistService;
	@Inject("ShopService") shopService!: ShopService;
	@Inject("I18NService") i18NService!: I18NService;

	state!: State;

	initializing: boolean = false;

	constructor() {
		super();
		this.state = store;
	}

	async logout() {
		this.authService.logout();
		this.state.reset();
		this.cartService.reset();
		this.sendEvent(YFC.EVENT_USER, undefined);
		// load cart as anonymous
		try {
			const cart = await this.cartService.loadCart();
			this.state.cart = cart;
			this.sendEvent(YFC.EVENT_CART, cart);
		} catch (e) {
			this.sendEvent(YFC.EVENT_CART, undefined);
		}
	}

	sendEvent(key: string, value?: any) {
		const event = new CustomEvent('yfcEvent', { detail: { key, value } });
		window.dispatchEvent(event);
	}

	async beforeMount() {
		if (typeof window !== 'undefined') {
			this.initializing = true;
			if (!window.yfcMutex) window.yfcMutex = new Mutex();
			const release = await window.yfcMutex.acquire();
			try {
				if (this.initializing) {
					// load i18n
					try {
						StorageManager.set(YFC.I18N_COOKIE, this.$i18n.locale)
						const messages = await this.i18NService.getFile(this.$i18n.locale);
						window.yfcI18nMessages = messages;
						this.sendEvent(YFC.EVENT_I18N, messages);
					} catch (e) {}
					// load user if exists a uid
					const uid = this.authService.getUid();
					if (uid) {
						try {
							const user = await this.authService.getLoggedUser();
							const customer = plainToClass(Customer, classToPlain(user));
							this.state.user = customer;
							window.yfcUser = customer;
							this.sendEvent(YFC.EVENT_USER, customer);
						} catch (e) {}
					}
					// load cart (anonymous or user bounded)
					try {
						const cart = await this.cartService.loadCart();
						this.state.cart = cart;
						window.yfcCart = cart;
						this.sendEvent(YFC.EVENT_CART, cart);
					} catch (e) {}
					// init wishlist and shop
					try {
						const shopConf = await this.shopService.getShopConf();
						this.state.shopConf = shopConf;
						window.yfcStore = shopConf;
						await this.wishlistService.initShop(this.state.user);
						this.sendEvent(YFC.EVENT_SHOP_CONF, shopConf);
					} catch (e) {}
					// finish
					this.sendEvent(YFC.EVENT_INIT);
				} else {
					this.state.user = this.state.user || window.yfcUser;
					this.state.cart = this.state.cart || window.yfcCart;
					this.state.shopConf = this.state.shopConf || window.yfcStore;
					// i18n
					const i18nMessage = this.$i18n.getLocaleMessage(this.$i18n.locale);
					if (Object.keys(i18nMessage).length === 0) {
						this.$i18n.setLocaleMessage(this.$i18n.locale, window.yfcI18nMessages);
						setLocale(this.$i18n.locale);
					}
				}
			} finally {
				release();
			}
		}
	}

	protected onURLSearchParams() {}

	created() {
		if (typeof window !== 'undefined') {
			window.addEventListener('yfcEvent', this.onYFCEvent);
		}
	}

	beforeDestroy() {
		if (typeof window !== 'undefined') {
			window.removeEventListener('yfcEvent', this.onYFCEvent);
		}
	}

	private onYFCEvent(event: Event) {
		const detail = (event as CustomEvent).detail;
		if (detail.key === YFC.EVENT_USER) {
			this.state.user = detail.value;
		} else if (detail.key === YFC.EVENT_CART && detail.value) {
			this.cartService.setCartId(detail.value.opaqueid)
			this.state.cart = detail.value;
		} else if (detail.key === YFC.EVENT_SHOP_CONF) {
			this.state.shopConf = detail.value;
		} else if (detail.key === YFC.EVENT_INIT) {
			this.initializing = false;
		} else if (detail.key === YFC.EVENT_I18N) {
			this.$i18n.setLocaleMessage(this.$i18n.locale, detail.value);
			setLocale(this.$i18n.locale);
		} else if (detail.key === YFC.EVENT_URL_SEARCH_PARAMS) {
			this.onURLSearchParams();
		}
		this.$emit(detail.key, detail.value);
	}
}